import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

import {
  Inbox,
  Bot,
  Workflow,
  Radio,
  ShieldCheck,
  BarChart3,
  CheckCircle2,
} from "lucide-react";

import platformImg from "../../../../Assets/Images/whatsapp/platform.png";
import inboxImg from "../../../../Assets/Images/whatsapp/inboxImg.png";
import automationImg from "../../../../Assets/Images/whatsapp/automation.png";
import broadcastImg from "../../../../Assets/Images/whatsapp/broadcasting.png";
import governanceImg from "../../../../Assets/Images/whatsapp/cx-governance.png";
import aianlytics from "../../../../Assets/Images/whatsapp/ai-anlaytics.png";
import aiAgentsImg from "../../../../Assets/Images/whatsapp/ai-agents.png";

// ============================================================
// PLATFORM FEATURES
// ============================================================

const features = [
  {
    id: 1,
    title: "UNIFIED INBOX",
    icon: Inbox,
    image: inboxImg,
  },
  {
    id: 2,
    title: "AI AGENTS",
    icon: Bot,
    image: aiAgentsImg,
  },
  {
    id: 3,
    title: "AUTOMATION",
    icon: Workflow,
    image: automationImg,
  },
  {
    id: 4,
    title: "BROADCASTING",
    icon: Radio,
    image: broadcastImg,
  },
  {
    id: 5,
    title: "AI GOVERNANCE",
    icon: ShieldCheck,
    image: governanceImg,
  },
  {
    id: 6,
    title: "AI ANALYTICS",
    icon: BarChart3,
    image: aianlytics,
  },
];

// ============================================================
// UNIFIED PLATFORM FEATURES
// ============================================================

const unifiedFeatures = [
  {
    title: "Shared Team Inbox",
    desc: "Collaborate using one centralized workspace.",
  },
  {
    title: "Customer Timeline",
    desc: "View complete interaction history.",
  },
  {
    title: "Chat Assignment",
    desc: "Assign conversations automatically.",
  },
  {
    title: "Internal Notes",
    desc: "Collaborate privately with teammates.",
  },
  {
    title: "Labels & Filters",
    desc: "Organize chats intelligently.",
  },
  {
    title: "Real-Time Notifications",
    desc: "Never miss an important message.",
  },
];

// ============================================================
// AUTOMATION DATA
// ============================================================

const automationCards = [
  {
    icon: "🤖",
    title: "AI-Powered Automation",
    desc: "Automate repetitive conversations with AI-driven workflows that provide instant, accurate responses while qualifying leads and routing chats to the right teams.",
    items: [
      "AI Chatbot",
      "Lead Qualification",
      "Auto Replies",
      "Department Routing",
      "Quick Replies",
      "AI Suggestions",
    ],
  },
  {
    icon: "📢",
    title: "Broadcast Campaigns",
    desc: "Reach thousands of customers with personalized WhatsApp campaigns, scheduled broadcasts, and real-time campaign performance tracking.",
    items: [
      "Bulk Campaigns",
      "Audience Segmentation",
      "Scheduled Messages",
      "Rich Media Support",
      "Campaign Analytics",
      "High Delivery Rate",
    ],
  },
  {
    icon: "👥",
    title: "Shared Team Inbox",
    desc: "Enable your sales and support teams to collaborate from one centralized inbox, ensuring every customer receives quick and consistent responses.",
    items: [
      "Multi-Agent Access",
      "Chat Assignment",
      "Internal Notes",
      "Conversation Labels",
      "Customer Timeline",
      "Role-Based Permissions",
    ],
  },
  {
    icon: "📊",
    title: "Analytics & CRM Integration",
    desc: "Measure performance with detailed analytics while seamlessly integrating WhatsApp conversations into your existing CRM and business tools.",
    items: [
      "Conversation Analytics",
      "Agent Performance",
      "Delivery Reports",
      "CRM Integration",
      "Webhook Support",
      "Custom API Access",
    ],
  },
];

// ============================================================
// COMPONENT
// ============================================================

export default function FeaturesSection() {
  const [activeTab, setActiveTab] = useState(features[0]);

  // ============================================================
  // PRELOAD ALL FEATURE IMAGES
  // ============================================================

  useEffect(() => {
    features.forEach((feature) => {
      const img = new Image();
      img.src = feature.image;
    });
  }, []);

  // ============================================================
  // AUTO ROTATION
  // ============================================================

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTab((prevTab) => {
        const currentIndex = features.findIndex(
          (feature) => feature.id === prevTab.id
        );

        const nextIndex = (currentIndex + 1) % features.length;

        return features[nextIndex];
      });
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="
        relative
        isolate
        w-full
        overflow-hidden
        bg-[var(--bg-primary)]
        text-[var(--text-primary)]
        font-saira
        transition-colors
        duration-500
      "
    >
      {/* ========================================================
          PLATFORM FEATURES
      ======================================================== */}

      <section
        className="
          relative
          overflow-hidden
          bg-[var(--bg-primary)]
          text-[var(--text-primary)]
          pt-5
          pb-8
          font-saira
          transition-colors
          duration-500

          min-[375px]:pt-6
          min-[375px]:pb-9

          sm:pt-6
          sm:pb-10
        "
      >
        {/* ======================================================
            BACKGROUND GLOWS
        ====================================================== */}

        <div
          className="
            pointer-events-none
            absolute
            -left-40
            top-24
            h-[250px]
            w-[250px]
            rounded-full
            bg-green-500/10
            blur-[130px]

            sm:h-[300px]
            sm:w-[300px]
            sm:blur-[150px]

            lg:h-[350px]
            lg:w-[350px]
            lg:blur-[180px]
          "
        />

        <div
          className="
            pointer-events-none
            absolute
            right-[-80px]
            bottom-0
            h-[220px]
            w-[220px]
            rounded-full
            bg-green-500/10
            blur-[130px]

            sm:right-[-50px]
            sm:h-[260px]
            sm:w-[260px]

            lg:right-0
            lg:h-[300px]
            lg:w-[300px]
            lg:blur-[180px]
          "
        />

        <div
          className="
            relative
            mx-auto
            w-full
            max-w-7xl
            px-4

            min-[375px]:px-5

            sm:px-6

            lg:px-8
          "
        >
          {/* ====================================================
              HEADING
          ==================================================== */}

          <motion.div
            initial={{
              opacity: 0,
              y: 30,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.2,
            }}
            transition={{
              duration: 0.6,
            }}
            className="
              mx-auto
              max-w-3xl
              text-center
            "
          >
            <span
              className="
                text-[10px]
                font-semibold
                uppercase
                tracking-[2.5px]
                text-green-400

                min-[375px]:text-[11px]
                min-[375px]:tracking-[3px]

                sm:text-xs
                sm:tracking-[3.5px]

                md:text-sm
                md:tracking-[4px]
              "
            >
              Platform Features
            </span>

            <h2
              className="
                mt-2
                text-[28px]
                font-bold
                leading-tight
                text-[var(--text-primary)]
                transition-colors
                duration-500

                min-[375px]:text-[30px]

                sm:text-4xl

                lg:text-5xl
              "
            >
              Explore the Platform
            </h2>

            <p
              className="
                mx-auto
                mt-2
                max-w-2xl
                text-[13px]
                leading-5
                text-[var(--text-muted)]
                transition-colors
                duration-500

                min-[375px]:text-sm
                min-[375px]:leading-6

                sm:text-base
                sm:leading-7
              "
            >
              Click on any feature to instantly preview how Webbocket helps
              your business manage WhatsApp communication.
            </p>
          </motion.div>

          {/* ====================================================
              PLATFORM CONTAINER
          ==================================================== */}

          <motion.div
            initial={{
              opacity: 0,
              y: 35,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.15,
            }}
            transition={{
              delay: 0.2,
            }}
            className="
              mt-5
              overflow-hidden
              rounded-2xl
              border
              border-[var(--border-primary)]

              /* PREMIUM GRAY CARD */
              bg-[var(--card-bg-secondary)]

              shadow-[0_10px_40px_rgba(0,0,0,0.04)]

              transition-all
              duration-500

              min-[375px]:mt-6
              min-[375px]:rounded-[20px]

              sm:rounded-[22px]

              lg:mt-6
              lg:rounded-[24px]
            "
          >
            {/* ==================================================
                TABS / FEATURE MENU
            ================================================== */}

            <div
              className="
                grid
                grid-cols-3

                sm:grid-cols-3

                lg:grid-cols-6
              "
            >
              {features.map((feature) => {
                const Icon = feature.icon;
                const active = activeTab.id === feature.id;

                return (
                  <button
                    key={feature.id}
                    type="button"
                    onClick={() => setActiveTab(feature)}
                    className={`
                      relative
                      flex
                      min-h-[52px]
                      min-w-0
                      flex-col
                      items-center
                      justify-center
                      gap-1
                      border-r
                      border-b
                      border-[var(--border-primary)]
                      px-1
                      py-1.5
                      transition-all
                      duration-300

                      max-[374px]:min-h-[50px]
                      max-[374px]:gap-0.5
                      max-[374px]:px-0.5
                      max-[374px]:py-1

                      min-[375px]:min-h-[56px]
                      min-[375px]:gap-1
                      min-[375px]:px-1.5
                      min-[375px]:py-2

                      sm:min-h-[70px]
                      sm:gap-1.5
                      sm:px-2
                      sm:py-2.5

                      md:min-h-[74px]
                      md:py-3

                      lg:min-h-0
                      lg:gap-2
                      lg:border-b-0
                      lg:py-2

                      ${
                        active
                          ? "bg-green-500/15"
                          : "bg-[var(--card-bg-secondary)] hover:bg-green-500/10"
                      }
                    `}
                  >
                    {/* Active Indicator */}

                    {active && (
                      <motion.div
                        layoutId="feature-active"
                        className="
                          absolute
                          bottom-0
                          left-0
                          h-[2px]
                          w-full
                          bg-green-400

                          sm:h-[3px]
                        "
                      />
                    )}

                    {/* Icon */}

                    <Icon
                      className={`
                        h-[14px]
                        w-[14px]
                        shrink-0

                        max-[374px]:h-[13px]
                        max-[374px]:w-[13px]

                        min-[375px]:h-4
                        min-[375px]:w-4

                        sm:h-[18px]
                        sm:w-[18px]

                        md:h-5
                        md:w-5

                        ${
                          active
                            ? "text-green-400"
                            : "text-[var(--text-muted)]"
                        }
                      `}
                    />

                    {/* Title */}

                    <span
                      className={`
                        block
                        w-full
                        truncate
                        px-0.5
                        text-center
                        text-[7.5px]
                        font-semibold
                        leading-3

                        max-[374px]:text-[7px]
                        max-[374px]:leading-[10px]

                        min-[375px]:text-[8.5px]
                        min-[375px]:leading-3

                        sm:text-[10px]
                        sm:leading-4

                        md:text-[11px]

                        lg:text-xs

                        ${
                          active
                            ? "text-[var(--text-primary)]"
                            : "text-[var(--text-muted)]"
                        }
                      `}
                    >
                      {feature.title}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* ==================================================
                FEATURE IMAGE
            ================================================== */}

            <div
              className="
                relative

                /* SLIGHT GRAY INNER SURFACE */
                bg-[var(--card-bg-secondary)]

                p-1.5
                transition-colors
                duration-500

                max-[374px]:p-1

                min-[375px]:p-2

                sm:p-3

                lg:p-4
              "
            >
              <div
                className="
                  relative
                  w-full
                  overflow-hidden
                  rounded-lg

                  /* KEEP IMAGE AREA CLEAN */
                  bg-white

                  shadow-[0_4px_20px_rgba(0,0,0,0.04)]

                  h-[220px]

                  max-[374px]:h-[210px]
                  max-[374px]:rounded-md

                  min-[375px]:h-[260px]
                  min-[375px]:rounded-lg

                  sm:h-[330px]
                  sm:rounded-xl

                  md:h-[390px]

                  lg:h-[480px]
                  lg:rounded-2xl

                  xl:h-[520px]
                "
              >
                {/* ==================================================
                    SMOOTH IMAGE TRANSITION
                ================================================== */}

                <AnimatePresence initial={false} mode="sync">
                  <motion.div
                    key={activeTab.id}
                    className="
                      absolute
                      inset-0
                      flex
                      items-center
                      justify-center
                      p-1

                      min-[375px]:p-2

                      sm:p-3

                      lg:p-4
                    "
                    initial={{
                      opacity: 0,
                      scale: 0.985,
                      x: 12,
                    }}
                    animate={{
                      opacity: 1,
                      scale: 1,
                      x: 0,
                    }}
                    exit={{
                      opacity: 0,
                      scale: 1.015,
                      x: -12,
                    }}
                    transition={{
                      opacity: {
                        duration: 0.45,
                        ease: "easeInOut",
                      },
                      scale: {
                        duration: 0.55,
                        ease: [0.22, 1, 0.36, 1],
                      },
                      x: {
                        duration: 0.55,
                        ease: [0.22, 1, 0.36, 1],
                      },
                    }}
                  >
                    <motion.img
                      src={activeTab.image}
                      alt={activeTab.title}
                      loading="eager"
                      draggable="false"
                      className="
                        block
                        h-full
                        w-full
                        max-h-full
                        max-w-full
                        select-none
                        object-contain
                      "
                    />
                  </motion.div>
                </AnimatePresence>

                {/* IMAGE OVERLAY */}

                <div
                  className="
                    pointer-events-none
                    absolute
                    inset-0
                    bg-gradient-to-b
                    from-transparent
                    via-transparent
                    to-black/[0.04]
                  "
                />
              </div>
            </div>
          </motion.div>

          {/* ====================================================
              BOTTOM GLOW
          ==================================================== */}

          <div
            className="
              pointer-events-none
              absolute
              bottom-0
              left-1/2
              h-40
              w-[300px]
              -translate-x-1/2
              rounded-full
              bg-green-500/10
              blur-[130px]

              sm:h-48
              sm:w-[450px]

              lg:h-56
              lg:w-[600px]
              lg:blur-[180px]
            "
          />
        </div>
      </section>

      {/* ========================================================
          UNIFIED COMMUNICATION PLATFORM
      ======================================================== */}

      <section
        className="
          relative
          overflow-hidden
          bg-[var(--bg-primary)]
          text-[var(--text-primary)]
          py-10
          font-saira
          transition-colors
          duration-500

          min-[375px]:py-12

          sm:py-14

          md:py-16

          lg:py-16
        "
      >
        {/* Background Glow */}

        <div
          className="
            pointer-events-none
            absolute
            right-[-80px]
            top-20
            h-56
            w-56
            rounded-full
            bg-green-500/10
            blur-[120px]

            sm:h-64
            sm:w-64

            lg:right-0
            lg:h-72
            lg:w-72
            lg:blur-[140px]
          "
        />

        <div
          className="
            relative
            mx-auto
            w-full
            max-w-7xl
            px-4

            min-[375px]:px-5

            sm:px-6

            lg:px-8
          "
        >
          <div
            className="
              grid
              grid-cols-1
              items-center
              gap-10

              sm:gap-12

              md:gap-14

              lg:grid-cols-2
              lg:gap-16
            "
          >
            {/* ==================================================
                IMAGE
            ================================================== */}

            <motion.div
              initial={{
                opacity: 0,
                x: -70,
              }}
              whileInView={{
                opacity: 1,
                x: 0,
              }}
              viewport={{
                once: true,
                amount: 0.15,
              }}
              transition={{
                duration: 0.8,
              }}
              className="
                relative
                mx-auto
                w-full
                max-w-[650px]
                min-w-0
              "
            >
              <img
                src={platformImg}
                alt="WhatsApp Business Platform"
                loading="lazy"
                className="
                  block
                  h-auto
                  w-full
                  rounded-2xl
                  border
                  border-[var(--border-primary)]
                  object-contain

                  shadow-[0_0_50px_rgba(37,211,102,0.12)]

                  transition-all
                  duration-500

                  min-[375px]:rounded-[22px]

                  sm:rounded-3xl
                  sm:shadow-[0_0_60px_rgba(37,211,102,0.14)]

                  lg:min-h-[520px]
                  lg:object-cover
                  lg:shadow-[0_0_80px_rgba(37,211,102,0.15)]
                "
              />

              {/* Floating Conversations Card */}

              <motion.div
                animate={{
                  y: [0, -10, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="
                  absolute
                  right-2
                  top-3
                  max-w-[145px]

                  rounded-xl
                  border
                  border-green-500/20

                  /* PREMIUM GRAY CARD */
                  bg-[var(--card-bg-secondary)]

                  px-2.5
                  py-2

                  shadow-[0_10px_35px_rgba(0,0,0,0.08)]

                  backdrop-blur-xl

                  transition-all
                  duration-500

                  min-[375px]:right-3
                  min-[375px]:top-4
                  min-[375px]:max-w-[165px]
                  min-[375px]:px-3
                  min-[375px]:py-2.5

                  sm:right-4
                  sm:top-5
                  sm:max-w-[190px]
                  sm:px-3.5
                  sm:py-3

                  md:right-5
                  md:max-w-[210px]

                  lg:-right-5
                  lg:top-8
                  lg:max-w-none
                  lg:rounded-2xl
                  lg:px-5
                  lg:py-4
                "
              >
                <h4
                  className="
                    text-[11px]
                    font-semibold
                    leading-4
                    text-green-400

                    min-[375px]:text-xs

                    sm:text-sm

                    lg:text-base
                  "
                >
                  Live Conversations
                </h4>

                <p
                  className="
                    mt-1
                    text-[9px]
                    leading-4
                    text-[var(--text-muted)]
                    transition-colors
                    duration-500

                    min-[375px]:text-[10px]

                    sm:text-xs

                    lg:mt-2
                    lg:text-sm
                  "
                >
                  1,245 Active Chats
                </p>
              </motion.div>
            </motion.div>

            {/* ==================================================
                CONTENT
            ================================================== */}

            <motion.div
              initial={{
                opacity: 0,
                x: 70,
              }}
              whileInView={{
                opacity: 1,
                x: 0,
              }}
              viewport={{
                once: true,
                amount: 0.15,
              }}
              transition={{
                duration: 0.8,
              }}
              className="min-w-0"
            >
              <span
                className="
                  text-[10px]
                  font-semibold
                  uppercase
                  tracking-[2.5px]
                  text-green-400

                  min-[375px]:text-[11px]
                  min-[375px]:tracking-[3px]

                  sm:text-xs
                  sm:tracking-[3.5px]

                  md:text-sm
                  md:tracking-[4px]
                "
              >
                Unified Platform
              </span>

              <h2
                className="
                  mt-3
                  text-[28px]
                  font-bold
                  leading-[1.15]
                  text-[var(--text-primary)]
                  transition-colors
                  duration-500

                  min-[375px]:text-[30px]

                  sm:mt-4
                  sm:text-4xl

                  md:text-[42px]

                  lg:text-5xl
                "
              >
                One Dashboard for Every Customer Conversation
              </h2>

              <p
                className="
                  mt-3
                  text-[13px]
                  leading-6
                  text-[var(--text-muted)]
                  transition-colors
                  duration-500

                  min-[375px]:text-sm
                  min-[375px]:leading-6

                  sm:mt-4
                  sm:text-base
                  sm:leading-7
                "
              >
                Simplify customer engagement with one centralized
                communication platform for sales, marketing and support
                teams.
              </p>

              {/* Feature List */}

              <div
                className="
                  mt-6
                  grid
                  grid-cols-1
                  gap-4

                  min-[375px]:mt-7
                  min-[375px]:gap-5

                  sm:mt-8
                  sm:grid-cols-2
                  sm:gap-x-6
                  sm:gap-y-5

                  lg:gap-x-8
                  lg:gap-y-5
                "
              >
                {unifiedFeatures.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{
                      opacity: 0,
                      y: 20,
                    }}
                    whileInView={{
                      opacity: 1,
                      y: 0,
                    }}
                    viewport={{
                      once: true,
                    }}
                    transition={{
                      delay: index * 0.08,
                    }}
                    whileHover={{
                      y: -3,
                    }}
                    className="
                      flex
                      min-w-0
                      gap-2.5

                      sm:gap-3
                    "
                  >
                    <div
                      className="
                        flex
                        h-8
                        w-8
                        shrink-0
                        items-center
                        justify-center
                        rounded-lg
                        bg-green-500/15

                        sm:h-9
                        sm:w-9
                        sm:rounded-xl
                      "
                    >
                      <CheckCircle2
                        className="text-green-400"
                        size={17}
                      />
                    </div>

                    <div className="min-w-0">
                      <h4
                        className="
                          text-[15px]
                          font-semibold
                          leading-5
                          text-[var(--text-primary)]
                          transition-colors
                          duration-500

                          sm:text-base

                          lg:text-lg
                        "
                      >
                        {item.title}
                      </h4>

                      <p
                        className="
                          mt-1
                          text-[11px]
                          leading-5
                          text-[var(--text-muted)]
                          transition-colors
                          duration-500

                          min-[375px]:text-xs

                          sm:text-sm
                          sm:leading-6
                        "
                      >
                        {item.desc}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Stats */}

              <div
                className="
                  mt-7
                  grid
                  grid-cols-3
                  gap-2
                  border-t
                  border-[var(--border-primary)]
                  pt-5
                  transition-colors
                  duration-500

                  min-[375px]:mt-8
                  min-[375px]:gap-3
                  min-[375px]:pt-6

                  sm:gap-5

                  lg:mt-8
                  lg:gap-6
                "
              >
                <div className="min-w-0">
                  <h3
                    className="
                      text-[24px]
                      font-bold
                      leading-none
                      text-green-400

                      min-[375px]:text-[27px]

                      sm:text-3xl

                      lg:text-4xl
                    "
                  >
                    99.9%
                  </h3>

                  <p
                    className="
                      mt-1.5
                      text-[9px]
                      leading-4
                      text-[var(--text-muted)]

                      min-[375px]:text-[10px]

                      sm:mt-2
                      sm:text-xs

                      lg:text-sm
                    "
                  >
                    Platform Uptime
                  </p>
                </div>

                <div className="min-w-0">
                  <h3
                    className="
                      text-[24px]
                      font-bold
                      leading-none
                      text-green-400

                      min-[375px]:text-[27px]

                      sm:text-3xl

                      lg:text-4xl
                    "
                  >
                    50K+
                  </h3>

                  <p
                    className="
                      mt-1.5
                      text-[9px]
                      leading-4
                      text-[var(--text-muted)]

                      min-[375px]:text-[10px]

                      sm:mt-2
                      sm:text-xs

                      lg:text-sm
                    "
                  >
                    Daily Messages
                  </p>
                </div>

                <div className="min-w-0">
                  <h3
                    className="
                      text-[24px]
                      font-bold
                      leading-none
                      text-green-400

                      min-[375px]:text-[27px]

                      sm:text-3xl

                      lg:text-4xl
                    "
                  >
                    24/7
                  </h3>

                  <p
                    className="
                      mt-1.5
                      text-[9px]
                      leading-4
                      text-[var(--text-muted)]

                      min-[375px]:text-[10px]

                      sm:mt-2
                      sm:text-xs

                      lg:text-sm
                    "
                  >
                    Expert Support
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========================================================
          SMART AUTOMATION
      ======================================================== */}

      <section
        className="
          relative
          overflow-hidden
          bg-[var(--bg-primary)]
          text-[var(--text-primary)]
          py-10
          font-saira
          transition-colors
          duration-500

          min-[375px]:py-12

          sm:py-14

          md:py-16

          lg:py-16
        "
      >
        {/* Background Glow */}

        <div
          className="
            pointer-events-none
            absolute
            left-1/2
            top-0
            h-56
            w-56
            -translate-x-1/2
            rounded-full
            bg-green-500/10
            blur-[120px]

            sm:h-64
            sm:w-64

            lg:h-72
            lg:w-72
            lg:blur-[140px]
          "
        />

        <div
          className="
            relative
            mx-auto
            w-full
            max-w-7xl
            px-4

            min-[375px]:px-5

            sm:px-6

            lg:px-8
          "
        >
          {/* ====================================================
              HEADING
          ==================================================== */}

          <motion.div
            initial={{
              opacity: 0,
              y: 40,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.2,
            }}
            transition={{
              duration: 0.7,
            }}
            className="
              mx-auto
              max-w-3xl
              text-center
            "
          >
            <span
              className="
                text-[10px]
                font-semibold
                uppercase
                tracking-[2.5px]
                text-green-400

                min-[375px]:text-[11px]
                min-[375px]:tracking-[3px]

                sm:text-xs
                sm:tracking-[3.5px]

                md:text-sm
                md:tracking-[4px]
              "
            >
              Smart Automation
            </span>

            <h2
              className="
                mt-3
                text-[27px]
                font-bold
                leading-[1.15]
                text-[var(--text-primary)]
                transition-colors
                duration-500

                min-[375px]:text-[29px]

                sm:mt-4
                sm:text-4xl

                md:text-[42px]

                lg:text-5xl
                lg:leading-tight
              "
            >
              Automate Customer Engagement Without Losing the Human Touch
            </h2>

            <p
              className="
                mt-4
                text-[13px]
                leading-6
                text-[var(--text-muted)]
                transition-colors
                duration-500

                min-[375px]:text-sm

                sm:mt-5
                sm:text-base
                sm:leading-7
              "
            >
              Improve customer experiences through AI-powered automation,
              personalized messaging, and intelligent workflows.
            </p>
          </motion.div>

          {/* ====================================================
              AUTOMATION CARDS
          ==================================================== */}

          <div
            className="
              mt-8
              space-y-4

              min-[375px]:mt-9
              min-[375px]:space-y-5

              sm:mt-11
              sm:space-y-6

              lg:mt-14
            "
          >
            {automationCards.map((card, index) => (
              <motion.div
                key={index}
                initial={{
                  opacity: 0,
                  y: 35,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                  amount: 0.1,
                }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.15,
                }}
                whileHover={{
                  y: -4,
                  borderColor: "rgba(34,197,94,0.35)",
                }}
                className="
                  group
                  relative
                  overflow-hidden
                  rounded-2xl
                  border
                  border-[var(--border-primary)]

                  /* PREMIUM GRAY CARD */
                  bg-[var(--card-bg-secondary)]

                  p-4

                  shadow-[0_10px_35px_rgba(0,0,0,0.035)]

                  transition-all
                  duration-300

                  min-[375px]:p-5
                  min-[375px]:rounded-[20px]

                  sm:rounded-2xl
                  sm:p-6

                  lg:rounded-3xl
                  lg:p-8
                "
              >
                {/* Card Glow */}

                <div
                  className="
                    pointer-events-none
                    absolute
                    -right-16
                    -top-16
                    h-32
                    w-32
                    rounded-full
                    bg-green-500/10
                    blur-[80px]
                    opacity-0
                    transition-opacity
                    duration-500
                    group-hover:opacity-100

                    sm:h-36
                    sm:w-36

                    lg:h-40
                    lg:w-40
                    lg:blur-[90px]
                  "
                />

                {/* Card Content */}

                <div
                  className="
                    relative
                    flex
                    flex-col
                    gap-5

                    sm:gap-6

                    lg:flex-row
                    lg:items-center
                    lg:justify-between
                    lg:gap-8
                  "
                >
                  {/* Left Content */}

                  <div
                    className="
                      flex
                      min-w-0
                      gap-3

                      min-[375px]:gap-4

                      sm:gap-5
                    "
                  >
                    {/* Icon */}

                    <div
                      className="
                        flex
                        h-11
                        w-11
                        shrink-0
                        items-center
                        justify-center
                        rounded-xl
                        bg-green-500/15
                        text-xl

                        min-[375px]:h-12
                        min-[375px]:w-12
                        min-[375px]:text-2xl

                        sm:h-14
                        sm:w-14
                        sm:rounded-2xl
                        sm:text-2xl
                        lg:h-16
                        lg:w-16
                        lg:text-3xl
                      "
                     >
                      {card.icon}
                    </div>

                    {/* Text */}

                    <div className="min-w-0 max-w-xl">
                      <h3
                        className="
                          text-[18px]
                          font-bold
                          leading-6
                          text-[var(--text-primary)]
                          transition-colors
                          duration-500

                          min-[375px]:text-xl

                          sm:text-2xl
                        "
                      >
                        {card.title}
                      </h3>

                      <p
                        className="
                          mt-2
                          text-[12px]
                          leading-5
                          text-[var(--text-muted)]
                          transition-colors
                          duration-500

                          min-[375px]:text-[13px]
                          min-[375px]:leading-6

                          sm:mt-3
                          sm:text-sm
                          sm:leading-6

                          lg:leading-7
                        "
                      >
                        {card.desc}
                      </p>
                    </div>
                  </div>

                  {/* Right Feature List */}

                  <div
                    className="
                      grid
                      w-full
                      grid-cols-1
                      gap-y-2.5

                      min-[375px]:grid-cols-2
                      min-[375px]:gap-x-4
                      min-[375px]:gap-y-3

                      sm:gap-x-6
                      sm:gap-y-3.5

                      md:gap-x-8

                      lg:min-w-[420px]
                      lg:gap-x-8
                      lg:gap-y-4
                    "
                  >
                    {card.items.map((item, i) => (
                      <motion.div
                        key={i}
                        initial={{
                          opacity: 0,
                          x: -15,
                        }}
                        whileInView={{
                          opacity: 1,
                          x: 0,
                        }}
                        viewport={{
                          once: true,
                        }}
                        transition={{
                          delay: i * 0.05,
                        }}
                        className="
                          flex
                          min-w-0
                          items-center
                          gap-2

                          sm:gap-2.5

                          lg:gap-3
                        "
                      >
                        <CheckCircle2
                          className="
                            h-4
                            w-4
                            shrink-0
                            text-green-400

                            sm:h-[17px]
                            sm:w-[17px]

                            lg:h-[18px]
                            lg:w-[18px]
                          "
                        />

                        <span
                          className="
                            min-w-0
                            text-[11px]
                            leading-4
                            text-[var(--text-secondary)]
                            transition-colors
                            duration-500

                            min-[375px]:text-xs

                            sm:text-sm
                          "
                        >
                          {item}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}